import { ref, computed } from 'vue'
import { useRecord } from '@/composables/useRecord'
import { dateTimeNow } from '@/utils'
import { useStore } from '@/store'
import { useRouter } from 'vue-router'
import { post } from '@/api'

export function useWorkflowRecord({ modelName = 'workflows', id }) {
  const store = useStore()
  const router = useRouter()
  const appName = 'workflows'
  const modelType = 'default'

  const isWorkflow = computed(() => modelName === 'workflows')
  const isWorkflowRun = computed(() => modelName === 'workflowruns')

  const {
    record,
    id: recordId,
    name: recordName,
    recordStatusKey,
    recordStatus,
    canEdit,
    canDelete,
    isDeleted,
    errorData,
    loadRecord,
    isLoadingRecord,
    isRecordLoaded,
    removeField,
  } = useRecord({
    appName,
    modelName,
    modelType,
    id,
  })

  const isRemoteWorkflow = computed(
    () => record.value?.repository_is_remote?.value,
  )
  const remoteRepositoryUrl = computed(
    () => record.value?.remote_repository?.value || '',
  )
  const importedFromROCrate = computed(
    () => record.value?.imported_from_rocrate?.value,
  )
  const workflowVersions = computed(
    () => record.value.workflowversions?.value || [],
  )
  const latestVersion = computed(
    () => workflowVersions.value?.find(({ is_latest }) => is_latest) || null,
  )
  const latestVersionId = computed(() => latestVersion.value?.id || null)
  const isLatestReleased = computed(
    () => latestVersion.value?.commit_hash !== '',
  )
  const canAddVersion = computed(
    () =>
      !latestVersion.value ||
      latestVersion.value.deleted ||
      isLatestReleased.value,
  )

  const isCreatingWorkflowVersion = ref(false)
  async function createWorkflowVersion() {
    const newWfBaseUrl = 'workflows/workflowversions/'
    const payload = {
      name: { value: { name: `Version ${dateTimeNow()}` } },
      workflow: { value: { id: recordId.value } },
    }
    try {
      isCreatingWorkflowVersion.value = true
      const response = await post(newWfBaseUrl, {
        data: { results: payload },
      })
      if (response.status === 201) {
        // redirect to new version
        const id = response.data?.results?.id?.value
        if (id)
          router.push({
            name: 'workflowVersionDetail',
            params: { modelType, id },
            query: { edit: true },
          })
      } else {
        throw new Error('Could not create version')
      }
    } catch (error) {
      store.commit('notify', {
        type: 'error',
        message:
          error?.response?.data?.message ||
          error?.message ||
          'Could not create version',
        isDismissable: true,
      })
    } finally {
      isCreatingWorkflowVersion.value = false
    }
  }

  return {
    record,
    recordId,
    recordName,
    recordStatusKey,
    recordStatus,
    canEdit,
    canDelete,
    isDeleted,
    errorData,
    loadRecord,
    isLoadingRecord,
    isRecordLoaded,
    removeField,
    //
    isWorkflow,
    isWorkflowRun,
    isRemoteWorkflow,
    remoteRepositoryUrl,
    importedFromROCrate,
    workflowVersions,
    latestVersion,
    latestVersionId,
    isLatestReleased,
    canAddVersion,
    createWorkflowVersion,
    isCreatingWorkflowVersion,
  }
}
